import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, BarChart3, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	intelligenceApi,
	type BenchmarkType,
	type IntelligencePeriodPreset,
} from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import {
	CommandCentreFilters,
	type IntelligencePeriodFilterValue,
} from "@/components/intelligence/CommandCentreFilters";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { formatCount, formatInr, formatPercentChange } from "@/lib/intelligenceFormat";
import { formatBenchmarkSource } from "@/lib/benchmarkSourceLabel";
import { cn } from "@/lib/utils";

type Props = { basePath?: string };

const BENCHMARK_TYPE_LABELS: Record<BenchmarkType, string> = {
	HOSPITAL_VS_GROUP: "Hospital vs group",
	HOSPITAL_VS_CHAIN: "Hospital vs chain",
	HOSPITAL_VS_PEER: "Hospital vs peer group",
	DEPARTMENT_VS_HOSPITAL: "Department vs hospital",
	DOCTOR_VS_DEPARTMENT: "Doctor vs department",
	DOCTOR_VS_HOSPITAL: "Doctor vs hospital",
	PERIOD_VS_PREVIOUS: "Period vs previous",
	PERIOD_VS_TARGET: "Period vs target",
};

function formatMetricValue(metricKey: string, value: number | null): string {
	if (value === null) return "—";
	if (
		metricKey.includes("revenue") ||
		metricKey.includes("billing") ||
		metricKey.includes("collections") ||
		metricKey.includes("outstanding")
	) {
		return formatInr(value, 0);
	}
	return formatCount(value);
}

function directionClass(direction: string | null): string {
	if (direction === "ABOVE") return "text-emerald-700";
	if (direction === "BELOW") return "text-rose-700";
	return "text-muted-foreground";
}

export default function BenchmarkIntelligencePage({ basePath }: Props) {
	const navBase = basePath ?? "/hospital-admin";
	const { user, activeHospitalId, isChainAdmin } = useAuth();
	const isSuperAdmin = user?.role === "SUPER_ADMIN";
	const isInvestor = user?.role === "INVESTOR";
	const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
	const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
	const chainHospitals = user?.hospitals ?? [];
	const isChainScoped = isChainAdmin || investorHasChainScope;

	const defaultFinancialHospitalId = useMemo(
		() =>
			defaultFinancialReportHospitalId({
				isSuperAdmin,
				isChainScoped,
				activeHospitalId,
				fallbackHospitalId: chainHospitals[0]?.id ?? null,
			}),
		[isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
	);

	const [period, setPeriod] = useState<IntelligencePeriodFilterValue>({ preset: "this_month" });
	const [selectedHospitalId, setSelectedHospitalId] = useState("");
	const [benchmarkTypeFilter, setBenchmarkTypeFilter] = useState<string>("");

	useEffect(() => {
		if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
			setSelectedHospitalId(defaultFinancialHospitalId || "all");
		}
	}, [defaultFinancialHospitalId, selectedHospitalId]);

	const { data: superHospitals } = useQuery({
		queryKey: ["super-admin-hospitals-benchmarks"],
		queryFn: () => superAdminApi.getHospitals(),
		enabled: isSuperAdmin,
	});

	const hospitalOptions = useMemo(() => {
		if (isSuperAdmin && superHospitals?.length) {
			return [
				{ id: "all", name: "All hospitals (consolidated)" },
				...superHospitals.map((h) => ({ id: h.id, name: h.name })),
			];
		}
		if (isChainScoped && chainHospitals.length) {
			return [
				{ id: "all", name: "All hospitals (consolidated)" },
				...chainHospitals.map((h) => ({ id: h.id, name: h.name })),
			];
		}
		return [];
	}, [isSuperAdmin, superHospitals, isChainScoped, chainHospitals]);

	const customReady =
		period.preset !== "custom" || (!!period.from && !!period.to && period.from <= period.to);

	const reportHospitalId = useMemo(
		() =>
			intelligenceReportHospitalId({
				showHospitalFilter,
				selectedHospitalId,
				isChainScoped,
			}),
		[showHospitalFilter, selectedHospitalId, isChainScoped]
	);

	const queryParams = useMemo(
		() => ({
			period: period.preset as IntelligencePeriodPreset,
			from: period.from,
			to: period.to,
			hospitalId: reportHospitalId,
			benchmarkType: benchmarkTypeFilter
				? (benchmarkTypeFilter as BenchmarkType)
				: undefined,
		}),
		[period, reportHospitalId, benchmarkTypeFilter]
	);

	const { data, isLoading, isError, error, isFetching } = useQuery({
		queryKey: ["intelligence-benchmarks", queryParams],
		queryFn: () => intelligenceApi.getBenchmarks(queryParams),
		enabled: customReady,
	});

	const benchmarkTypes = data?.meta?.availableBenchmarkTypes ?? [];

	return (
		<div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6">
			<IntelligenceSubNav basePath={navBase} />

			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
						<BarChart3 className="h-7 w-7 text-indigo-600" />
						Benchmarks
					</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Compare hospital, department, and doctor performance against peers, prior periods, and targets.
						{data?.scope.hospitalName ? ` · ${data.scope.hospitalName}` : ""}
					</p>
				</div>
				{isFetching && !isLoading && (
					<span className="text-xs text-muted-foreground inline-flex items-center gap-1">
						<Loader2 className="h-3 w-3 animate-spin" /> Updating…
					</span>
				)}
			</div>

			<CommandCentreFilters
				hospitals={hospitalOptions}
				showHospitalFilter={showHospitalFilter}
				hospitalId={selectedHospitalId}
				onHospitalChange={setSelectedHospitalId}
				period={period}
				onPeriodChange={setPeriod}
			/>

			{data && customReady && (
				<div className="flex justify-end">
					<IntelligenceServerExportButton
						report="benchmarks"
						params={queryParams}
						filename={`benchmarks-${data.period.label.replace(/[^\w-]+/g, "-").toLowerCase()}`}
					/>
				</div>
			)}

			<div className="flex flex-wrap items-center gap-2">
				<label htmlFor="benchmark-type" className="text-sm text-muted-foreground">
					Benchmark type
				</label>
				<select
					id="benchmark-type"
					value={benchmarkTypeFilter}
					onChange={(e) => setBenchmarkTypeFilter(e.target.value)}
					className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
				>
					<option value="">All applicable types</option>
					{benchmarkTypes.map((t) => (
						<option key={t} value={t}>
							{BENCHMARK_TYPE_LABELS[t] ?? t}
						</option>
					))}
				</select>
			</div>

			{!customReady && (
				<Card className="border-amber-200 bg-amber-50/50">
					<CardContent className="pt-4 text-sm">
						<p className="font-medium text-amber-900">Select a valid custom date range</p>
					</CardContent>
				</Card>
			)}

			{isLoading && customReady && (
				<div className="flex items-center justify-center py-24 text-muted-foreground gap-2">
					<Loader2 className="h-5 w-5 animate-spin" />
					Loading benchmarks…
				</div>
			)}

			{isError && customReady && (
				<Card className="border-destructive/50 bg-destructive/5">
					<CardContent className="pt-4 text-sm">
						<p className="font-medium text-destructive flex items-center gap-2">
							<AlertTriangle className="h-4 w-4" />
							Unable to load benchmarks
						</p>
						<p className="text-muted-foreground mt-1">
							{(error as Error)?.message ?? "Check your access permissions and try again."}
						</p>
					</CardContent>
				</Card>
			)}

			{data && customReady && (
				<>
					<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
						<Card>
							<CardHeader className="pb-1">
								<CardTitle className="text-xs text-muted-foreground">OK</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-2xl font-semibold tabular-nums">{data.summary.ok}</p>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="pb-1">
								<CardTitle className="text-xs text-muted-foreground">Above</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-2xl font-semibold tabular-nums text-emerald-700">
									{data.summary.above}
								</p>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="pb-1">
								<CardTitle className="text-xs text-muted-foreground">Below</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-2xl font-semibold tabular-nums text-rose-700">
									{data.summary.below}
								</p>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="pb-1">
								<CardTitle className="text-xs text-muted-foreground">At benchmark</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-2xl font-semibold tabular-nums">{data.summary.at}</p>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="pb-1">
								<CardTitle className="text-xs text-muted-foreground">Insufficient data</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-2xl font-semibold tabular-nums">{data.summary.insufficientData}</p>
							</CardContent>
						</Card>
						<Card>
							<CardHeader className="pb-1">
								<CardTitle className="text-xs text-muted-foreground">Not applicable</CardTitle>
							</CardHeader>
							<CardContent>
								<p className="text-2xl font-semibold tabular-nums">{data.summary.notApplicable}</p>
							</CardContent>
						</Card>
					</div>

					{data.limitations.length > 0 && (
						<Card className="border-slate-200 bg-slate-50/80">
							<CardContent className="pt-4 text-sm text-muted-foreground">
								{data.limitations.join(" ")}
							</CardContent>
						</Card>
					)}

					<Card>
						<CardHeader>
							<CardTitle className="text-base">Benchmark results · {data.period.label}</CardTitle>
						</CardHeader>
						<CardContent className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Metric</TableHead>
										<TableHead>Type</TableHead>
										<TableHead className="text-right">Current</TableHead>
										<TableHead className="text-right">Benchmark</TableHead>
										<TableHead className="text-right">Variance</TableHead>
										<TableHead>Direction</TableHead>
										<TableHead className="text-right">Rank</TableHead>
										<TableHead>Source</TableHead>
										<TableHead>Status</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.benchmarks.length === 0 && (
										<TableRow>
											<TableCell colSpan={9} className="text-center text-muted-foreground py-8">
												No benchmarks available for this scope and period.
											</TableCell>
										</TableRow>
									)}
									{data.benchmarks.map((b, idx) => (
										<TableRow key={`${b.metricKey}-${b.benchmarkType}-${idx}`}>
											<TableCell className="font-medium">{b.metricLabel}</TableCell>
											<TableCell className="text-xs text-muted-foreground">
												{BENCHMARK_TYPE_LABELS[b.benchmarkType] ?? b.benchmarkType}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatMetricValue(b.metricKey, b.currentValue)}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{formatMetricValue(b.metricKey, b.benchmarkValue)}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{b.percentageVariance !== null
													? formatPercentChange(b.percentageVariance)
													: "—"}
											</TableCell>
											<TableCell className={cn("text-sm", directionClass(b.direction))}>
												{b.direction ?? "—"}
											</TableCell>
											<TableCell className="text-right tabular-nums">
												{b.rank !== null && b.sampleSize > 0
													? `${b.rank} / ${b.sampleSize}`
													: "—"}
											</TableCell>
											<TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">
												{formatBenchmarkSource(b.benchmarkSource)}
											</TableCell>
											<TableCell className="text-xs">{b.status}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}
